import {
  Divider,
  Icons,
  MetricsBox,
  Skeleton,
  Stack,
  StatusChipV2,
  tokens,
  Typography,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { dashesOrNum, formatPercentage } from '@cast/utils';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { CardCurrentDate } from '../_components/CardCurrentDate';
import { useSavingsSummaryData } from './_hooks/useSavingsSummaryData';

const CARD_TITLE = 'Workload Autoscaler savings';

const FOOTER_TEXT = 'cost reduction';

const formatAmount = (amount: number | undefined) =>
  `$${dashesOrNum({ value: amount, roundBy: 2 })}`;

const SavingsSummaryCardSkeleton = () => (
  <Stack height="100%" gap={16} justifyContent="space-between">
    <Stack gap={16} sx={{ flexShrink: 0 }}>
      <MetricsBox
        size="m"
        testId="savings-summary-available-skeleton"
        slots={{
          label: <Skeleton variant="text" width={60} />,
          value: <Skeleton variant="text" width={120} />,
          unit: <Skeleton variant="text" width={32} />,
          footer: <Skeleton variant="text" width={160} />,
        }}
      />

      <Divider />
    </Stack>

    <MetricsBox
      size="m"
      testId="savings-summary-realized-skeleton"
      slots={{
        label: <Skeleton variant="text" width={60} />,
        value: <Skeleton variant="text" width={120} />,
        unit: <Skeleton variant="text" width={32} />,
        footer: <Skeleton variant="text" width={160} />,
      }}
    />
  </Stack>
);

const SavingsTrend = ({ percentage }: { percentage: number | undefined }) => (
  <StatusChipV2
    color="success"
    size="small"
    variant="strong"
    label={formatPercentage(percentage, { roundBy: 0 })}
    slots={{
      icon: <Icons.ArrowDownRight />,
    }}
    sx={{ borderRadius: tokens.theme.radius.full }}
  />
);

const SavingsMetric = ({
  label,
  amount,
  percentage,
  amountColor,
  testId,
}: {
  label: string;
  amount: number | undefined;
  percentage: number | undefined;
  amountColor?: string;
  testId: string;
}) => (
  <MetricsBox
    testId={testId}
    size="m"
    slots={{
      label,
      value: formatAmount(amount),
      unit: '/mo',
      footer: (
        <Stack direction="row" gap={8} alignItems="center">
          <SavingsTrend percentage={percentage} />
          <Typography variant="P12R" color="textSecondary">
            {FOOTER_TEXT}
          </Typography>
        </Stack>
      ),
    }}
    slotProps={{
      value: {
        variant: 'metricmd',
        color: amountColor,
      },
      unit: {
        variant: 'P12R',
      },
    }}
  />
);

export const SavingsSummaryCard = () => {
  const { data, isLoading, isFetching, error, refetch } =
    useSavingsSummaryData();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  return (
    <WidgetLayout
      title={CARD_TITLE}
      testId="savings-summary-card"
      headerDivider={false}
      headerEndContent={<CardCurrentDate />}
      slotProps={{ header: { py: 12 } }}
    >
      {isCardLoading ? (
        <SavingsSummaryCardSkeleton />
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load savings summary"
          refresh={refetch}
        />
      ) : (
        <Stack height="100%" gap={16} justifyContent="space-between">
          <Stack
            gap={16}
            data-testid="savings-summary-card-summary"
            sx={{ flexShrink: 0 }}
          >
            <SavingsMetric
              testId="savings-summary-available"
              label="available"
              amount={data?.estimatedMonthlySavings}
              percentage={data?.estimatedMonthlySavingsPercentage}
            />

            <Divider />
          </Stack>

          <SavingsMetric
            testId="savings-summary-realized"
            label="realized"
            amount={data?.currentMonthlySavings}
            percentage={data?.currentMonthlySavingsPercentage}
            amountColor={tokens.theme.fg.status.success}
          />
        </Stack>
      )}
    </WidgetLayout>
  );
};
